"use client";

import { useState } from "react";
import type { Block } from "../types";
import type { MediaItem } from "../types";
import { ImageField } from "./media";
import { langProps, type EditLang } from "./locale";
import { BTN_GHOST, IconButton, INPUT } from "./ui";

// The article editor: a list of blocks (paragraph, heading, list, quote, image) to add, edit, move and remove.

type Kind = "p" | "h2" | "h3" | "list" | "ol" | "quote" | "image";
export type EditorBlock = { key: string; kind: Kind; text: string; image: { url: string; alt: string } | null; caption: string };

const KINDS: { kind: Kind; label: string }[] = [
  { kind: "p", label: "Paragraph" },
  { kind: "h2", label: "Heading" },
  { kind: "h3", label: "Subheading" },
  { kind: "list", label: "Bullet list" },
  { kind: "ol", label: "Numbered list" },
  { kind: "quote", label: "Quote" },
  { kind: "image", label: "Image" },
];

let counter = 0;
const key = () => `b${Date.now().toString(36)}${(counter++).toString(36)}`;

export const toEditor = (blocks: Block[]): EditorBlock[] =>
  blocks.map((b) => {
    const base = { key: key(), text: "", image: null, caption: "" };
    if ("h2" in b) return { ...base, kind: "h2", text: b.h2 };
    if ("h3" in b) return { ...base, kind: "h3", text: b.h3 };
    if ("quote" in b) return { ...base, kind: "quote", text: b.quote };
    if ("list" in b) return { ...base, kind: "list", text: b.list.join("\n") };
    if ("ol" in b) return { ...base, kind: "ol", text: b.ol.join("\n") };
    if ("image" in b) return { ...base, kind: "image", image: { url: b.image.url, alt: b.image.alt }, caption: b.image.caption ?? "" };
    return { ...base, kind: "p", text: b.p };
  });

export const fromEditor = (blocks: EditorBlock[]): Block[] =>
  blocks.flatMap((b): Block[] => {
    const items = b.text.split("\n").map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "").trim()).filter(Boolean);
    switch (b.kind) {
      case "h2":
        return b.text.trim() ? [{ h2: b.text.trim() }] : [];
      case "h3":
        return b.text.trim() ? [{ h3: b.text.trim() }] : [];
      case "quote":
        return b.text.trim() ? [{ quote: b.text.trim() }] : [];
      case "list":
        return items.length ? [{ list: items }] : [];
      case "ol":
        return items.length ? [{ ol: items }] : [];
      case "image":
        return b.image?.url ? [{ image: { url: b.image.url, alt: b.image.alt, caption: b.caption.trim() || undefined } }] : [];
      default:
        return b.text.trim() ? [{ p: b.text.trim() }] : [];
    }
  });

export function BlockEditor({ value, onChange, media, lang = "en" }: { value: EditorBlock[]; onChange: (value: EditorBlock[]) => void; media: MediaItem[]; lang?: EditLang }) {
  const [help, setHelp] = useState(false);
  const text = langProps(lang);
  const update = (i: number, changes: Partial<EditorBlock>) => onChange(value.map((b, j) => (j === i ? { ...b, ...changes } : b)));
  const move = (i: number, by: number) => {
    const next = [...value];
    const [b] = next.splice(i, 1);
    next.splice(i + by, 0, b);
    onChange(next);
  };
  const add = (kind: Kind, at = value.length) => {
    const next = [...value];
    next.splice(at, 0, { key: key(), kind, text: "", image: null, caption: "" });
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-bold">Article *</span>
        <button type="button" onClick={() => setHelp((h) => !h)} aria-expanded={help} className="py-2 text-sm text-teal underline underline-offset-4">
          Formatting help
        </button>
      </div>
      {help && (
        <div className="rounded-xl bg-teal/10 p-4 text-sm leading-relaxed">
          In any text: <code>**bold**</code>, <code>*italic*</code>, <code>[link text](https://example.com)</code>. Links to other pages on the site can start
          with <code>/</code>, e.g. <code>[our services](/services)</code>. Lists: one item per line.
        </div>
      )}

      {value.map((b, i) => (
        <div key={b.key} className="rounded-xl border border-midnight/10 bg-offwhite p-3 sm:p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <select
              aria-label={`Block ${i + 1} type`}
              value={b.kind}
              onChange={(e) => update(i, { kind: e.target.value as Kind })}
              className="min-h-11 rounded-lg border border-midnight/20 bg-offwhite px-3 text-sm font-bold"
            >
              {KINDS.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {k.label}
                </option>
              ))}
            </select>
            <span className="flex-1" />
            <IconButton label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</IconButton>
            <IconButton label="Move down" disabled={i === value.length - 1} onClick={() => move(i, 1)}>↓</IconButton>
            <IconButton label="Remove block" onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</IconButton>
          </div>

          {b.kind === "image" ? (
            <div className="flex flex-col gap-3">
              <ImageField label="Image" value={b.image} onChange={(image) => update(i, { image })} media={media} />
              <input aria-label="Caption (optional)" placeholder="Caption (optional)" value={b.caption} onChange={(e) => update(i, { caption: e.target.value })} className={INPUT} {...text} />
            </div>
          ) : b.kind === "h2" || b.kind === "h3" ? (
            <input aria-label={b.kind === "h2" ? "Heading" : "Subheading"} value={b.text} onChange={(e) => update(i, { text: e.target.value })} className={`${INPUT} text-lg font-bold`} placeholder={b.kind === "h2" ? "Heading" : "Subheading"} {...text} />
          ) : (
            <textarea
              aria-label={KINDS.find((k) => k.kind === b.kind)?.label}
              rows={b.kind === "p" ? 4 : 3}
              value={b.text}
              onChange={(e) => update(i, { text: e.target.value })}
              placeholder={b.kind === "list" || b.kind === "ol" ? "One item per line" : b.kind === "quote" ? "Quote" : "Write a paragraph…"}
              className={`${INPUT} resize-y leading-relaxed ${b.kind === "quote" ? "italic" : ""}`}
              {...text}
            />
          )}
        </div>
      ))}

      <div className="flex flex-wrap gap-2 rounded-xl border border-dashed border-midnight/20 p-3">
        <span className="w-full text-sm text-midnight/70">Add a block:</span>
        {KINDS.map((k) => (
          <button key={k.kind} type="button" onClick={() => add(k.kind)} className={BTN_GHOST}>
            + {k.label}
          </button>
        ))}
      </div>
    </div>
  );
}
